/**
 * 漢数字・全角数字 → 半角整数の変換。
 *
 * 位取り式（「二二〇七」= 2207）と、位取り漢字を伴う書式（「二千二百七」= 2207）の
 * 両方に対応する。大字（壱弐参…）も受け付ける。
 */

const KANJI_DIGITS: Record<string, number> = {
  〇: 0,
  零: 0,
  '○': 0,
  '０': 0,
  一: 1,
  壱: 1,
  壹: 1,
  弌: 1,
  二: 2,
  弐: 2,
  貳: 2,
  弍: 2,
  三: 3,
  参: 3,
  參: 3,
  弎: 3,
  四: 4,
  肆: 4,
  五: 5,
  伍: 5,
  六: 6,
  陸: 6,
  七: 7,
  柒: 7,
  漆: 7,
  八: 8,
  捌: 8,
  九: 9,
  玖: 9,
};

const KANJI_UNITS: Record<string, number> = {
  十: 10,
  拾: 10,
  百: 100,
  佰: 100,
  千: 1000,
  仟: 1000,
};

const KANJI_MYRIADS: Record<string, number> = {
  万: 10_000,
  萬: 10_000,
  億: 100_000_000,
};

export const KANJI_NUMBER_CHARS =
  Object.keys(KANJI_DIGITS).join('') +
  Object.keys(KANJI_UNITS).join('') +
  Object.keys(KANJI_MYRIADS).join('');

/**
 * 数値表記を整数に変換する。変換できない場合は null。
 * 「元」は呼び出し側で 1 に読み替えるため、ここでは扱わない。
 */
export function parseJapaneseNumber(raw: string): number | null {
  const s = raw.replace(/[\s　]/g, '');
  if (s.length === 0) return null;

  // 半角数字のみ
  if (/^[0-9]+$/.test(s)) {
    const n = Number.parseInt(s, 10);
    return Number.isFinite(n) ? n : null;
  }

  const hasUnit = [...s].some((c) => c in KANJI_UNITS || c in KANJI_MYRIADS);

  if (!hasUnit) {
    // 位取り式: 二二〇七 → 2207
    let out = '';
    for (const c of s) {
      if (c in KANJI_DIGITS) out += String(KANJI_DIGITS[c]);
      else if (/[0-9]/.test(c)) out += c;
      else return null;
    }
    if (out.length === 0 || out.length > 12) return null;
    const n = Number.parseInt(out, 10);
    return Number.isFinite(n) ? n : null;
  }

  // 位取り漢字を伴う書式: 二千二百七 → 2207
  let total = 0;
  let section = 0;
  let current = 0;
  let sawAny = false;

  for (const c of s) {
    if (c in KANJI_DIGITS) {
      current = current * 10 + KANJI_DIGITS[c];
      sawAny = true;
    } else if (/[0-9]/.test(c)) {
      current = current * 10 + Number(c);
      sawAny = true;
    } else if (c in KANJI_UNITS) {
      const unit = KANJI_UNITS[c];
      section += (current === 0 ? 1 : current) * unit;
      current = 0;
      sawAny = true;
    } else if (c in KANJI_MYRIADS) {
      const myriad = KANJI_MYRIADS[c];
      total += (section + current) * myriad;
      section = 0;
      current = 0;
      sawAny = true;
    } else {
      return null;
    }
  }

  if (!sawAny) return null;
  const n = total + section + current;
  return Number.isFinite(n) ? n : null;
}
